import React from 'react';

import {NOTICE_TYPES} from 'src/features/lohp/hooks/useFetchHeroBanners';

import {ConsolidatedHeroBanner, ConsolidatedHeroBannerSkeleton} from '../consolidated-hero-banner';
import {MarketingHeroBannerProps} from '../marketing-hero-banner.react-component';

/**
 * Selects which hero banner to render based on the notice type the backend
 * returns. Both banners are permanent production paths:
 *   - NOTICE_TYPES.LOHP_HERO_BANNER ('lohp_hero_banner') -> ConsolidatedHeroBanner (carousel)
 *   - anything else (e.g. 'banner_carousel_slide', FALLBACK_BANNER) -> WrappedComponent (legacy)
 *
 * useFetchHeroBanners fires the lohp_hero_banner and banner_carousel_slide
 * notice requests in parallel and sets noticeType to whichever wins (preferring
 * lohp_hero_banner), so the backend decides per-request which banner a user sees.
 * While the requests are in flight (isLoading) the variant is unknown, so a
 * neutral skeleton is shown to avoid biasing the layout toward either banner.
 */
export const WithConsolidatedHeroBanner = (
    WrappedComponent: React.ComponentType<MarketingHeroBannerProps>,
) => {
    const WithConsolidatedWrapper = (defaultProps: MarketingHeroBannerProps) => {
        // We don't know which variant will win until the response lands, so use
        // the neutral skeleton during the fetch.
        if (defaultProps.isLoading) {
            return <ConsolidatedHeroBannerSkeleton className={defaultProps.className} />;
        }

        if (!defaultProps.heroBanners?.length) {
            return <WrappedComponent {...defaultProps} />;
        }

        if (defaultProps.noticeType !== NOTICE_TYPES.LOHP_HERO_BANNER) {
            return <WrappedComponent {...defaultProps} />;
        }

        return <ConsolidatedHeroBanner {...defaultProps} />;
    };

    return WithConsolidatedWrapper;
};
